import { addMonths, differenceInDays } from 'date-fns';
import React from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { fetchDateRange } from '../utils/api/reports/analytics-requests';
import { DashboardChartHeader } from './components/chart-header';
import { DashboardDatePicker } from './components/dashboard-date-picker';
import { DashboardLineChartType1 } from './components/dashboard-line-chart-type1';

// TODO Can we use the translation files somehow?
const workflowTypeLabels = {
  user_added: 'User is Added',
  user_added_or_updated: 'Created or Updated',
  user_updated: 'User is Updated',
  completed_content: 'Completed Content',
  completed_assignment: 'Completed an Assignment',
  registered_workshop: 'Workshop Registration',
  attended_workshop: 'Workshop Attendance',
  hired_today: 'Hired Today',
  hired_60: 'Hired 60 Days',
  hired_90: 'Hired 90 Days',
  hired_custom: 'Hired X Days',
};

function formatData(data) {
  if (data && Object.keys(data)) {
    const returnData = [];
    const workshopTypes = new Set(Object.keys(workflowTypeLabels));
    for (const workshopType of workshopTypes) {
      const newEntry = {
        id: workflowTypeLabels[workshopType] ?? workshopType,
        label: workflowTypeLabels[workshopType] ?? workshopType,
        data: Object.entries(data).map((d) => ({
          x: d[0],
          y: d[1][workshopType] || 0,
        })),
      };
      newEntry.sum = newEntry.data.reduce((accum, point) => {
        accum += point.y ?? 0;
      }, 0);

      // FOR FUTURE READERS
      // The following code is a weekly rollup, when we are ready to support the aggregation dropdown on the charts.
      // const buckets = {};
      // Object.entries(data).map((d) => {
      //   console.log('types d[0]', d[0]);
      //   const xDate = moment(d[0]);
      //   const xIndex = xDate.year() + ' W' + xDate.week();
      //   if (!buckets[xIndex]) {
      //     buckets[xIndex] = {
      //       x: xIndex, // Probably need a better name here
      //       y: 0,
      //     };
      //   }
      //   buckets[xIndex].y += d[1][workshopType] ?? 0;
      // });

      // const newEntry = {
      //   id: workflowTypeLabels[workshopType] ?? workshopType,
      //   label: workflowTypeLabels[workshopType] ?? workshopType,
      //   data: Object.values(buckets),
      // };
      returnData.push(newEntry);
    }
    returnData.sort((a, b) => b.id.localeCompare(a.id));
    return returnData;
  }
  return [];
}

export function WorkflowTypesTriggeredChart(props) {
  const defaultDateRange = [addMonths(new Date(), -1), new Date()];
  const [dateRange, setDateRange] = React.useState(defaultDateRange);
  const [data, setData] = React.useState([]);
  const [dateRangeError, setDateRangeError] = React.useState('');
  const [showDatePicker, setShowDatePicker] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      if (!dateRange) {
        // If the date range is empty, force it to the default date range and do it again.
        setDateRange(defaultDateRange);
        return;
      }
      const daysDiff = differenceInDays(dateRange[1], dateRange[0]);
      if (daysDiff > 366) {
        setDateRangeError('Date range too large.  Please select a range of 1 year or less.');
        return;
      }
      setDateRangeError('');
      const jsonData = await fetchDateRange(
        props.reportClient,
        '/api/v1/analytics/workflow-types-triggered-counts',
        dateRange
      );
      const formattedData = formatData(jsonData);
      setData(formattedData);

      // We delay showing the datepicker until the rest of the UI is ready in order 
      // to avoid a snapping in the UI as it resizes itself.
      setShowDatePicker(true);
    })();
  }, [dateRange]);

  return (
    <div className="chart-box-flow">
      <div className="chart-box-flow-header">
        <DashboardChartHeader
          title="Workflow Types"
          tooltip="Workflow breakdown by audience types."
        />
        <DashboardDatePicker {...{ showDatePicker, dateRange, setDateRange, dateLocale: props.dateLocale }} />
      </div>
      {dateRangeError && <div className="ui message chart-date-error">{dateRangeError}</div>}
      <div className="chart-box-autosizer-wrapper">
        <AutoSizer>
          {({ width, height }) => (
            <DashboardLineChartType1
              data={data}
              width={width}
              height={height}
              margin={{ left: 40, bottom: 20, top: 10, right: 210 }}
              singleLegendProps={{
                anchor: 'top-right',
                direction: 'column',
                justify: false,
                translateX: 240,
                translateY: 0,
                itemsSpacing: 5,
                itemWidth: 200,
                itemHeight: 16,
                itemTextColor: '#999',
                itemDirection: 'left-to-right',
              }}
            />
          )}
        </AutoSizer>
      </div>
    </div>
  );
}
